<template>
  <div class="user-details-page">
    <div class="filters-panel">
      <div class="filter-section">
        <h3>筛选条件</h3>
        <div class="filter-row">
          <div class="filter-label">登录位置：</div>
          <div class="location-filter">
            <select v-model="selectedCountry" class="location-select" @change="applyFilters">
              <option value="">选择国家或地区</option>
              <option v-for="country in uniqueCountries" :key="country" :value="country">{{ country }}</option>
            </select>
            <select v-if="selectedCountry" v-model="selectedProvince" class="location-select" @change="applyFilters">
              <option value="">选择省份</option>
              <option v-for="province in uniqueProvinces" :key="province" :value="province">{{ province }}</option>
            </select>
            <select v-if="selectedProvince" v-model="selectedCity" class="location-select" @change="applyFilters">
              <option value="">选择市</option>
              <option v-for="city in uniqueCities" :key="city" :value="city">{{ city }}</option>
            </select>
          </div>
        </div>
        
        <div class="filter-row">
          <div class="filter-label">最后登录时间：</div>
          <div class="date-filter">
            <input 
              type="date" 
              v-model="loginTimeRange.start" 
              class="date-input"
              @change="applyFilters"
            >
            <span class="date-separator">至</span>
            <input 
              type="date" 
              v-model="loginTimeRange.end" 
              class="date-input"
              @change="applyFilters"
            >
          </div>
        </div>
        
        <div class="filter-row">
          <div class="filter-label">登录天数：</div>
          <div class="days-filter">
            <input 
              type="number" 
              v-model="loginDaysRange.min" 
              min="0" 
              placeholder="最小值"
              class="days-input"
              @change="applyFilters"
            >
            <span class="days-separator">至</span>
            <input 
              type="number" 
              v-model="loginDaysRange.max" 
              min="0" 
              placeholder="最大值"
              class="days-input"
              @change="applyFilters"
            >
          </div>
        </div>
        
        <div class="filter-actions">
          <button class="export-button" @click="exportData">导出数据</button>
          <button class="reset-button" @click="resetFilters">重置筛选</button>
        </div>
      </div>
    </div>
    
    <div v-if="detailsRefreshError" class="details-refresh-error">{{ detailsRefreshError }}</div>
    
    <div class="user-list-container">
      <div class="user-list-header">
        <div class="left-controls">
          <div class="pagination">
            <button 
              class="page-button"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              上一页
            </button>
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <button 
              class="page-button"
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >
              下一页
            </button>
          </div>
          
          <div class="filter-controls">
            <label>每页显示：</label>
            <select v-model="pageSize" @change="changePage(1)">
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
        
        <div class="right-controls">
          <button class="refresh-btn" @click="refreshUserActivityDetails" :disabled="refreshingDetails">
            {{ refreshingDetails ? '刷新中...' : '刷新数据' }}
          </button>
          <p class="update-time">更新时间：{{ updateTime }}</p>
        </div>
      </div>
      
      <div v-if="loading" class="loading-indicator">加载中...</div>
      <div v-else-if="filteredUsers.length > 0" class="user-table-container">
        <table class="user-table">
          <thead>
            <tr>
              <th>序号</th>
              <th @click="changeSort('account')">
                用户账号
                <span class="sort-icon" v-if="sortField === 'account'">
                  {{ sortOrder === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="changeSort('location')">
                最后登录位置
                <span class="sort-icon" v-if="sortField === 'location'">
                  {{ sortOrder === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="changeSort('lastLoginTime')">
                最后登录时间
                <span class="sort-icon" v-if="sortField === 'lastLoginTime'">
                  {{ sortOrder === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="changeSort('loginDays')">
                登录天数
                <span class="sort-icon" v-if="sortField === 'loginDays'">
                  {{ sortOrder === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(user, index) in paginatedUsers" :key="user.account">
              <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
              <td>{{ formatAccount(user.account) }}</td>
              <td>{{ user.location }}</td>
              <td>{{ user.lastLoginTime }}</td>
              <td>{{ user.loginDays }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="no-data">
        {{ userDetails.accounts && userDetails.accounts.length > 0 ? 
        '没有符合筛选条件的用户' : '暂无用户数据' }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'UserDetails',
  components: {},
  data() {
    return {
      loading: false,
      refreshingDetails: false,
      detailsRefreshError: null,
      error: null,
      updateTime: '',
      userDetails: {
        accounts: [],
        lastLoginLocations: [],
        lastLoginTimes: [],
        loginDays: []
      },
      pageSize: 20,
      currentPage: 1,
      sortOrder: 'desc',
      sortField: 'lastLoginTime', // 默认按最后登录时间排序
      
      // 筛选条件
      selectedCountry: '',
      selectedProvince: '',
      selectedCity: '',
      loginTimeRange: {
        start: '',
        end: ''
      },
      loginDaysRange: {
        min: '',
        max: ''
      },
      
      // 缓存所有用户数据
      allUsers: []
    }
  },
  computed: {
    // 获取所有不重复的国家列表
    uniqueCountries() {
      if (!this.hasValidUserDetails()) return [];
      return [...new Set(this.userDetails.lastLoginLocations.map(location => location.split('–')[0]))].sort();
    },
    
    // 获取所有不重复的省份列表
    uniqueProvinces() {
      if (!this.hasValidUserDetails() || !this.selectedCountry) return [];
      return [...new Set(this.userDetails.lastLoginLocations
        .filter(location => location.startsWith(this.selectedCountry + '–'))
        .map(location => location.split('–')[1]))].sort();
    },
    
    // 获取所有不重复的市列表
    uniqueCities() {
      if (!this.hasValidUserDetails() || !this.selectedProvince) return [];
      return [...new Set(this.userDetails.lastLoginLocations
        .filter(location => location.startsWith(this.selectedCountry + '–' + this.selectedProvince + '–'))
        .map(location => location.split('–')[2]))].sort();
    },
    
    // 根据筛选条件过滤用户
    filteredUsers() {
      if (!this.hasValidUserDetails()) return [];
      
      // 如果没有启用任何筛选条件，则返回所有用户
      if (
        !this.selectedCountry && 
        !this.selectedProvince && 
        !this.selectedCity && 
        !this.loginTimeRange.start && 
        !this.loginTimeRange.end && 
        this.loginDaysRange.min === '' && 
        this.loginDaysRange.max === ''
      ) {
        return this.sortedUsers;
      }
      
      return this.sortedUsers.filter(user => {
        // 登录位置筛选
        if (this.selectedCountry) {
          const locationParts = user.location.split('–');
          if (locationParts[0] !== this.selectedCountry) return false;
          if (this.selectedProvince && locationParts[1] !== this.selectedProvince) return false;
          if (this.selectedCity && locationParts[2] !== this.selectedCity) return false;
        }
        
        // 最后登录时间筛选
        if (this.loginTimeRange.start || this.loginTimeRange.end) {
          const loginDate = new Date(user.lastLoginTime);
          
          if (this.loginTimeRange.start) {
            const startDate = new Date(this.loginTimeRange.start);
            if (loginDate < startDate) return false;
          }
          
          if (this.loginTimeRange.end) {
            const endDate = new Date(this.loginTimeRange.end);
            endDate.setHours(23, 59, 59); // 设置为当天结束时间
            if (loginDate > endDate) return false;
          }
        }
        
        // 登录天数筛选
        const days = parseInt(user.loginDays);
        if (this.loginDaysRange.min !== '' && days < parseInt(this.loginDaysRange.min)) {
          return false;
        }
        if (this.loginDaysRange.max !== '' && days > parseInt(this.loginDaysRange.max)) {
          return false;
        }
        
        return true;
      });
    },
    
    // 根据排序字段和顺序排序用户
    sortedUsers() {
      if (!this.hasValidUserDetails()) return [];
      
      // 根据排序字段和顺序对索引进行排序
      let sortedIndices = this.userDetails.accounts.map((_, i) => i);
      
      if (this.sortField === 'account') {
        sortedIndices.sort((a, b) => {
          const valA = this.userDetails.accounts[a].toLowerCase();
          const valB = this.userDetails.accounts[b].toLowerCase();
          return this.sortOrder === 'asc' 
            ? valA.localeCompare(valB) 
            : valB.localeCompare(valA);
        });
      } else if (this.sortField === 'location') {
        sortedIndices.sort((a, b) => {
          const valA = this.userDetails.lastLoginLocations[a].toLowerCase();
          const valB = this.userDetails.lastLoginLocations[b].toLowerCase();
          return this.sortOrder === 'asc' 
            ? valA.localeCompare(valB) 
            : valB.localeCompare(valA);
        });
      } else if (this.sortField === 'lastLoginTime') {
        sortedIndices.sort((a, b) => {
          const timeA = new Date(this.userDetails.lastLoginTimes[a]).getTime();
          const timeB = new Date(this.userDetails.lastLoginTimes[b]).getTime();
          return this.sortOrder === 'asc' ? timeA - timeB : timeB - timeA;
        });
      } else if (this.sortField === 'loginDays') {
        sortedIndices.sort((a, b) => {
          const daysA = parseInt(this.userDetails.loginDays[a]);
          const daysB = parseInt(this.userDetails.loginDays[b]);
          return this.sortOrder === 'asc' ? daysA - daysB : daysB - daysA;
        });
      }
      
      // 转换为用户对象数组
      return sortedIndices.map(i => ({
        account: this.userDetails.accounts[i],
        location: this.userDetails.lastLoginLocations[i],
        lastLoginTime: this.userDetails.lastLoginTimes[i],
        loginDays: this.userDetails.loginDays[i]
      }));
    },
    
    // 当前页的用户
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = Math.min(start + this.pageSize, this.filteredUsers.length);
      return this.filteredUsers.slice(start, end);
    },
    
    // 总页数
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredUsers.length / this.pageSize));
    }
  },
  methods: {
    // 检查用户详情数据是否有效
    hasValidUserDetails() {
      return this.userDetails && 
             Array.isArray(this.userDetails.accounts) && 
             Array.isArray(this.userDetails.lastLoginLocations) &&
             Array.isArray(this.userDetails.lastLoginTimes) &&
             Array.isArray(this.userDetails.loginDays) &&
             this.userDetails.accounts.length > 0 &&
             this.userDetails.lastLoginLocations.length > 0 &&
             this.userDetails.lastLoginTimes.length > 0 &&
             this.userDetails.loginDays.length > 0;
    },
    
    // 获取带授权头的配置
    getAuthHeaders() {
      return {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      };
    },
    
    // 加载用户详情数据
    async loadUserDetails() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await axios.get('/api/users/activities/details', this.getAuthHeaders());
        if (response.data.code === 200) {
          this.userDetails = {
            accounts: response.data.data.accounts || [],
            lastLoginLocations: response.data.data.lastLoginLocations || [],
            lastLoginTimes: response.data.data.lastLoginTimes || [],
            loginDays: response.data.data.loginDays || []
          };
          this.updateTime = response.data.data.updateTime || new Date().toLocaleString();
        } else {
          this.error = response.data.message || '获取用户详情失败';
        }
      } catch (error) {
        console.error('获取用户详情失败:', error);
        this.error = error.message || '获取用户详情时发生错误';
      } finally {
        this.loading = false;
      }
    },
    
    // 添加格式化账号的方法
    formatAccount(account) {
      // 如果是手机号，返回加密的形式
      if (/^\d{11}$/.test(account)) {
        return account.substring(0, 3) + '****' + account.substring(7);
      }
      return account;
    },
    
    // 切换页面
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    
    // 改变排序方式
    changeSort(field) {
      if (this.sortField === field) {
        // 如果点击的是当前排序字段，则切换排序顺序
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        // 如果点击的是新字段，则设置为降序
        this.sortField = field;
        this.sortOrder = 'desc';
      }
      // 切换排序后，重置到第一页
      this.changePage(1);
    },
    
    // 应用筛选条件
    applyFilters() {
      // 重置到第一页
      this.changePage(1);
    },
    
    // 重置所有筛选条件
    resetFilters() {
      this.selectedCountry = '';
      this.selectedProvince = '';
      this.selectedCity = '';
      this.loginTimeRange = {
        start: '',
        end: ''
      };
      this.loginDaysRange = {
        min: '',
        max: ''
      };
      this.changePage(1);
    },
    
    // 导出数据方法
    exportData() {
      // 准备CSV数据
      const headers = ['用户账号', '最后登录位置', '最后登录时间', '登录天数'];
      const rows = this.filteredUsers.map(user => [
        user.account,
        user.location,
        user.lastLoginTime,
        user.loginDays
      ]);
      
      // 添加BOM头，确保Excel正确显示中文
      const BOM = '\uFEFF';
      const csvContent = BOM + [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      // 创建Blob对象
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      
      // 创建下载链接
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `用户数据_${new Date().toLocaleDateString()}.csv`;
      
      // 触发下载
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    
    // 刷新用户活动详情数据
    async refreshUserActivityDetails() {
      if (this.refreshingDetails) return;
      
      this.refreshingDetails = true;
      this.detailsRefreshError = null;
      
      try {
        const response = await axios.post('/api/users/activities/details/refresh', {}, this.getAuthHeaders());
        if (response.data.code === 200) {
          this.$emit('success-message', '用户活动详情数据刷新成功');
          // 刷新成功后重新加载用户数据
          await this.loadUserDetails();
        } else {
          // 只有非授权错误才设置刷新错误
          if (response.data.code !== 401 && response.data.code !== 403) {
            this.detailsRefreshError = response.data.message || '刷新用户活动详情数据失败';
          }
        }
      } catch (error) {
        console.error('刷新用户活动详情数据失败:', error);
        // 只显示非认证错误
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403)) {
          this.detailsRefreshError = `刷新用户活动详情失败: ${error.message}`;
        }
        this.handleAuthError();
      } finally {
        this.refreshingDetails = false;
      }
    },
    
    // 处理认证错误
    handleAuthError() {
      this.$emit('auth-error');
    }
  },
  mounted() {
    this.loadUserDetails();
  }
}
</script>

<style scoped>
.user-details-page {
  padding: 25px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  max-width: 1600px;
  margin: 0 auto;
}

/* 筛选面板样式 */
.filters-panel {
  margin-bottom: 20px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  overflow: hidden;
}

.filter-section {
  padding: 15px;
}

.filter-section h3 {
  font-size: 16px;
  color: #303133;
  margin-top: 0;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.filter-row {
  display: flex;
  margin-bottom: 15px;
  align-items: flex-start;
}

.filter-label {
  width: 120px;
  padding-top: 8px;
  font-weight: 500;
  color: #606266;
}

.location-filter {
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  gap: 10px;
  max-height: 120px;
  overflow-y: auto;
  padding: 5px;
}

.location-select {
  padding: 8px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  width: 150px;
  background-color: white;
  color: #606266;
  font-size: 14px;
  transition: all 0.3s;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}

.location-select:hover, 
.location-select:focus {
  border-color: #409EFF;
  outline: none;
}

.date-filter, .days-filter {
  display: flex;
  align-items: center;
}

.date-input {
  padding: 8px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  width: 150px;
}

.days-input {
  padding: 8px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  width: 80px;
}

.date-separator, .days-separator {
  margin: 0 10px;
  color: #909399;
}

.filter-actions {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  padding-top: 10px;
  margin-top: 10px;
  border-top: 1px dashed #ebeef5;
}

.user-list-container {
  margin-top: 15px;
  overflow-x: auto;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.user-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
  background-color: #f8fafc;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.left-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
}

.right-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.filter-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.filter-controls label {
  margin-right: 8px;
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
  font-weight: 500;
}

.filter-controls select {
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: white;
  color: #606266;
  font-size: 14px;
  transition: all 0.3s;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
}

.filter-controls select:hover, 
.filter-controls select:focus {
  border-color: #409EFF;
  outline: none;
}

.ml-20 {
  margin-left: 20px;
}

.pagination {
  display: flex;
  align-items: center;
  background-color: white;
  padding: 8px 12px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.page-button {
  padding: 8px 15px;
  background-color: white;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 80px;
  font-weight: 500;
  color: #606266;
}

.page-button:hover:not(:disabled) {
  color: #409EFF;
  border-color: #c6e2ff;
  background-color: #ecf5ff;
}

.page-button:disabled {
  cursor: not-allowed;
  color: #c0c4cc;
  background-color: #f5f7fa;
}

.page-info {
  margin: 0 15px;
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
  font-weight: 500;
}

.user-table-container {
  margin-top: 15px;
  overflow-x: auto;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.user-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: fixed;
  background-color: white;
}

.user-table th,
.user-table td {
  padding: 15px;
  text-align: left;
  border-bottom: 1px solid #ebeef5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-table th {
  font-weight: 600;
  color: #303133;
  background-color: #f5f7fa;
  position: sticky;
  top: 0;
  z-index: 10;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

.user-table th:hover {
  background-color: #e6f1ff;
  color: #409EFF;
}

.sort-icon {
  margin-left: 5px;
  font-weight: bold;
  color: #409EFF;
}

.user-table tr:hover td {
  background-color: #f0f7ff;
}

.user-table tr:last-child td {
  border-bottom: none;
}

.user-table tr:nth-child(even) {
  background-color: #fafafa;
}

.loading-indicator, .no-data {
  padding: 50px;
  text-align: center;
  color: #909399;
  font-size: 15px;
  background-color: #f8fafc;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  margin-top: 20px;
  border: 1px dashed #e0e3e9;
}

.details-refresh-error {
  padding: 10px;
  background-color: #f8fafc;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  margin-top: 20px;
  border: 1px dashed #e0e3e9;
  color: #f56c6c;
  font-size: 15px;
  text-align: center;
}

.update-time {
  font-size: 13px;
  color: #909399;
  margin: 0 0 0 10px;
  white-space: nowrap;
}

.refresh-btn {
  padding: 8px 15px;
  background-color: #409EFF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.refresh-btn:hover {
  background-color: #66b1ff;
}

.refresh-btn:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.export-button {
  padding: 8px 15px;
  background-color: #67c23a;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.export-button:hover {
  background-color: #85ce61;
}

.reset-button {
  padding: 8px 15px;
  background-color: #f56c6c;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.reset-button:hover {
  background-color: #f78989;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .user-details-page {
    padding: 15px;
  }

  .filter-row {
    flex-direction: column;
}

  .filter-label {
    width: 100%;
    margin-bottom: 8px;
  }
  
  .location-filter {
    max-height: 150px;
  }
  
  .date-filter, .days-filter {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
}

  .date-separator, .days-separator {
    margin: 5px 0;
}

  .date-input, .days-input {
    width: 100%;
  }

  .user-list-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .pagination {
    margin-top: 10px;
    width: 100%;
    justify-content: space-between;
  }
  
  .filter-controls {
    width: 100%;
    justify-content: space-between;
  }
  
  .ml-20 {
    margin-left: 0;
  }
  
  .user-table {
    font-size: 13px;
  }
  
  .user-table th,
  .user-table td {
    padding: 12px 10px;
  }
  
  .user-table th:nth-child(1),
  .user-table td:nth-child(1) {
    width: 60px;
  }
  
  .user-table th:nth-child(2),
  .user-table td:nth-child(2) {
    width: 40%;
  }
  
  .user-table th:nth-child(3),
  .user-table td:nth-child(3) {
    width: 60%;
  }
}

@media (max-width: 480px) {
  .user-details-page {
    padding: 10px;
  }

  .filter-section h3 {
    font-size: 15px;
  }

  .user-list-header {
    justify-content: space-between;
    margin-bottom: 15px;
  }
  
  .nav-button {
    padding: 8px 10px;
    font-size: 13px;
    margin-right: 5px;
  }
  
  .page-button {
    min-width: auto;
    padding: 6px 10px;
    font-size: 13px;
  }
  
  .page-info {
    font-size: 12px;
    margin: 0 8px;
  }
  
  .user-table th,
  .user-table td {
    padding: 10px 8px;
    font-size: 12px;
  }

  .loading-indicator, .no-data {
    padding: 30px 15px;
  }
}
</style> 